import { useCallback } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useExperience } from '@unisporkal/experiences';
import { QUERY_KEYS } from 'src/constants';
import {
  AgreementsFactory,
  type Agreements,
  type AgreementsResponse,
} from 'app/domain';
import useApi from './useApi';
import type AgreementsService from './agreementsService';

const agreementsFactory = new AgreementsFactory();

/**
 * The agreements query: its key and its fetcher, in one place.
 *
 * Exported so the route middleware can prefetch it before React runs. Matching
 * keys is the point — what the middleware fetches is dehydrated under the key
 * `useAgreements` later reads, so the client does not fetch it again.
 *
 * Takes the service rather than the http client, so the client stays inside
 * this directory — `createApi` is what turns one into the other, for the hook
 * and the middleware alike.
 *
 * The `select` below stays with the hook: the middleware wants the DTO to
 * dehydrate, not the model.
 */
export const agreementsQuery = (agreementsService: AgreementsService) => ({
  queryKey: QUERY_KEYS.agreements,
  queryFn: (): Promise<AgreementsResponse> => agreementsService.get(),
  // Stated rather than inherited. Federation's app-shell options default
  // queries to `staleTime: Infinity`, but the clients the hydration entries
  // build do not carry those options — so without this the prefetched
  // agreements hydrate stale and are fetched a second time on mount, which is
  // the round trip the shared key exists to avoid.
  staleTime: Infinity,
});

// The agreements a user holds, on their own query key.
//
// User-scoped: the payload does not depend on the current search, so this is
// fetched once per session and refetched only when something actually changes
// what the user holds — a download altering a quota or exhausting an agreement
// (see the invalidations in GalleryItems and BannersAdapter). Whether a given
// agreement applies to the search being run is derived separately, by
// `isAgreementApplicable`.
//
// Mounted at the page root so the fetch happens once, inside the root Suspense
// boundary, rather than in whichever consumer renders first.
const useAgreements = (): Agreements | undefined => {
  const { agreementsService } = useApi();
  const agencyUnlimited = useExperience('GettyAgencyUnlimited').isActive();

  // Memoized on the (session-stable) experience so React Query still gets a
  // stable `select` reference and keeps its result memo.
  const select = useCallback(
    (data: AgreementsResponse): Agreements | undefined =>
      data?.agreements
        ? agreementsFactory.fromDTO(data.agreements, agencyUnlimited)
        : undefined,
    [agencyUnlimited]
  );

  return useSuspenseQuery<AgreementsResponse, Error, Agreements | undefined>({
    ...agreementsQuery(agreementsService),
    select,
  }).data;
};

export default useAgreements;
